import React, { useState } from 'react';
import { Trash2, Phone, User } from 'lucide-react';
import { FeedbackBanner } from '../../components/ui/PageLayout';
import ImagePreviewModal from '../../components/ImagePreviewModal';
import api, { getAssetUrl, getApiErrorMessage } from '../../services/api';

const MoradoresTable = ({ moradores, loading, refreshData, onEdit }) => {
  const [imagemPreview, setImagemPreview] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [pending, setPending] = useState(false);
  
  const handleDelete = async (id) => {
    if (window.confirm("Deseja excluir este morador?")) {
      try {
        await api.delete(`/moradores/${id}`);
        refreshData(); 
      } catch (error) {
        setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Erro ao excluir o morador.') });
      }
    }
  };

  const handleToggleStatus = async (id) => {
    if (pending) return;
    const person = moradores.find((item) => item.id === id);
    if (person?.status && !window.confirm('Bloquear este morador e revogar seus acessos nos dispositivos?')) return;
    setPending(true); setFeedback(null);
    try {
      const { data } = await api.patch(`/moradores/${id}/toggle-status`);
      await refreshData();
      setFeedback({ type: 'success', message: data.message });
    } catch (error) {
      setFeedback({ type: 'error', message: getApiErrorMessage(error, 'Não foi possível alterar o status do morador.') });
    } finally { setPending(false); }
  };

  if (loading) return <div className="p-10 text-center text-gray-500 font-medium">Carregando moradores...</div>;

  return (
    // Adicionamos 'max-h-[500px]' para limitar a altura e 'overflow-y-auto' para o scroll
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200">
        <table className="w-full text-left border-collapse">
          {/* 'sticky top-0' faz o cabeçalho ficar parado enquanto você rola a lista */}
          <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm text-gray-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 font-semibold">Nome</th>
              <th className="px-6 py-4 font-semibold">Unidade</th>
              <th className="px-6 py-4 font-semibold">Contato</th>
              <th className="px-6 py-4 font-semibold text-center">Status</th>
              <th className="px-6 py-4 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {moradores.map((m) => (
              <tr key={m.id} className="hover:bg-gray-50/80 transition-colors group">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 overflow-hidden rounded-full bg-slate-100 flex items-center justify-center">
                      {m.foto_url ? (
                        <button
                          type="button"
                          onClick={() => setImagemPreview({ src: getAssetUrl(m.foto_url), alt: m.nome })}
                          className="h-full w-full cursor-zoom-in"
                          title="Visualizar foto"
                        >
                          <img src={getAssetUrl(m.foto_url)} alt={m.nome} className="h-full w-full object-cover" />
                        </button>
                      ) : (
                        <User size={18} className="text-slate-400" />
                      )}
                    </div>
                    <button type="button" onClick={() => onEdit(m)} className="text-left font-medium text-slate-700 transition-colors hover:text-blue-600 hover:underline" title="Editar morador">{m.nome}</button>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500 text-sm">{m.categoria_unidade_nome && <span className="block font-semibold text-slate-700">{m.categoria_unidade_nome}</span>}{m.unidade}</td>
                
                <td className="px-6 py-4 text-gray-500 text-sm">
                  {m.contato ? (
                    <a 
                      href={`https://wa.me/55${m.contato.replace(/\D/g,'')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title="Clique para abrir no WhatsApp"
                      className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 transition-colors"
                    >
                      <Phone size={14} />
                      {m.contato}
                    </a>
                  ) : (
                    <span className="text-gray-300">---</span>
                  )}
                </td>

                <td className="px-6 py-4 text-center">
                  <button 
                    onClick={() => handleToggleStatus(m.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all hover:scale-105 ${
                      m.status 
                        ? 'bg-green-100 text-green-600' 
                        : 'bg-red-100 text-red-600'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${m.status ? 'bg-green-600' : 'bg-red-600'}`}></span>
                    {m.status ? 'Ativo' : 'Inativo'}
                  </button>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handleDelete(m.id)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {moradores.length === 0 && (
              <tr>
                <td colSpan="5" className="px-6 py-20 text-center text-gray-400 italic">
                  Nenhum morador encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <FeedbackBanner type={feedback?.type} message={feedback?.message} onClose={() => setFeedback(null)} />
      <ImagePreviewModal image={imagemPreview} onClose={() => setImagemPreview(null)} />
    </div>
  );
};

export default MoradoresTable;
